const stripMathDelimiters = (value) => {
  if (!value) return "";
  let text = String(value).trim();
  text = text.replace(/^\$\$([\s\S]*)\$\$$/, "$1");
  text = text.replace(/^\$([\s\S]*)\$$/, "$1");
  text = text.replace(/^\\\(([\s\S]*)\\\)$/, "$1");
  text = text.replace(/^\\\[([\s\S]*)\\\]$/, "$1");
  return text.trim();
};

const replaceFrac = (text) => {
  let output = text;
  let prev = null;
  const fracRegex = /\\frac\s*\{([^{}]+)\}\{([^{}]+)\}/g;
  while (output !== prev) {
    prev = output;
    output = output.replace(fracRegex, "($1)/($2)");
  }
  return output;
};

const replaceSqrt = (text) => {
  let output = text;
  let prev = null;
  const sqrtRegex = /\\sqrt\s*\{([^{}]+)\}/g;
  while (output !== prev) {
    prev = output;
    output = output.replace(sqrtRegex, "sqrt($1)");
  }
  return output;
};

const replaceVariable = (text, variable, target = "x") => {
  if (!variable || variable === target) return text;
  const safeVar = String(variable).trim();
  if (!safeVar) return text;
  const regex = new RegExp(`(^|[^a-zA-Z])${safeVar}([^a-zA-Z]|$)`, "g");
  return text.replace(regex, `$1${target}$2`);
};

const normalizeInverseTrig = (text) => {
  let output = text;
  const inverseMap = [
    ["sin", "arcsin"],
    ["cos", "arccos"],
    ["tan", "arctan"],
  ];
  inverseMap.forEach(([src, dest]) => {
    const pattern = new RegExp(`\\\\${src}\\s*\\^\\s*\\{-1\\}`, "g");
    output = output.replace(pattern, dest);
  });

  output = output.replace(
    /\\cot\s*\^\s*\{-1\}\s*\(([^()]*)\)/g,
    "arctan(1/($1))"
  );
  output = output.replace(
    /\\cot\s*\^\s*\{-1\}\s*([a-zA-Z0-9]+)/g,
    "arctan(1/($1))"
  );
  return output;
};

const extractIntegrand = (latex) => {
  const match = latex.match(
    /\\int(?:_{[^}]*}|_[^\s^]+)?(?:\\^{[^}]*}|\^[^\s]+)?/
  );
  if (!match || match.index == null) return null;
  const after = latex.slice(match.index + match[0].length);
  const diffMatch = after.match(
    /(.*?)(?:\\,|\\!|\\;|\\:|\\s)*d([a-zA-Z])(?![a-zA-Z])/
  );
  if (!diffMatch) {
    return { expression: after.trim() || null, variable: null };
  }
  return {
    expression: diffMatch[1].trim(),
    variable: diffMatch[2],
  };
};

const detectSingleVariable = (latex) => {
  if (!latex) return null;
  const matches = latex
    .replace(/\\[a-zA-Z]+/g, " ")
    .match(/[a-zA-Z]/g);
  const vars = new Set(
    (matches || []).filter((letter) => !["e", "E", "i", "I"].includes(letter))
  );
  if (vars.size !== 1) return null;
  return Array.from(vars)[0];
};

const isEquation = (latex) => {
  if (!latex.includes("=")) return false;
  if (latex.match(/\\(leq|geq|le|ge|neq|approx|sim)/)) return false;
  return true;
};

export const latexToDesmosExpression = (latex, { variable } = {}) => {
  if (!latex) return "";
  let text = stripMathDelimiters(latex);
  if (!text) return "";

  text = text.replace(/\\dfrac/g, "\\frac");
  text = text.replace(/\\tfrac/g, "\\frac");
  text = normalizeInverseTrig(text);
  text = text.replace(/\\operatorname\{([^}]+)\}/g, "$1");
  text = text.replace(/\\text\{[^}]*\}/g, "");
  text = text.replace(/\\left/g, "");
  text = text.replace(/\\right/g, "");
  text = text.replace(/\\,|\\!|\\;|\\:|\\quad|\\qquad/g, "");
  text = replaceFrac(text);
  text = replaceSqrt(text);
  text = text.replace(/\^\s*\{([^{}]+)\}/g, "^($1)");
  text = text.replace(/\\leq/g, "<=");
  text = text.replace(/\\geq/g, ">=");
  text = text.replace(/\\le/g, "<=");
  text = text.replace(/\\ge/g, ">=");
  text = text.replace(/\\neq/g, "!=");
  text = text.replace(/\\ne/g, "!=");

  const replacements = [
    [/\\cdot/g, "*"],
    [/\\times/g, "*"],
    [/\\div/g, "/"],
    [/\\pi/g, "pi"],
    [/\\ln/g, "ln"],
    [/\\log/g, "log"],
    [/\\sin/g, "sin"],
    [/\\cos/g, "cos"],
    [/\\tan/g, "tan"],
    [/\\csc/g, "csc"],
    [/\\sec/g, "sec"],
    [/\\cot/g, "cot"],
    [/\\arcsin/g, "arcsin"],
    [/\\arccos/g, "arccos"],
    [/\\arctan/g, "arctan"],
  ];

  replacements.forEach(([pattern, replacement]) => {
    text = text.replace(pattern, replacement);
  });

  text = text.replace(/[{}]/g, (match) => (match === "{" ? "(" : ")"));
  text = text.replace(/\\\\/g, "");
  text = text.replace(/\s+/g, "");
  text = replaceVariable(text, variable, "x");

  return text.trim();
};

export const buildDesmosExpressions = ({
  latex,
  overlays,
} = {}) => {
  const cleaned = stripMathDelimiters(latex || "");
  if (!cleaned) return [];

  const integrand = extractIntegrand(cleaned);
  const baseExpression = integrand?.expression || cleaned;
  const variable = integrand?.variable || detectSingleVariable(baseExpression);
  const base = latexToDesmosExpression(baseExpression, { variable });
  if (!base) return [];

  const expressions = [];
  const baseLatex = isEquation(baseExpression) || base.includes("=")
    ? base
    : `f(x)=${base}`;
  expressions.push({ id: "base", latex: baseLatex });

  if (Array.isArray(overlays) && overlays.length) {
    overlays.forEach((overlay, index) => {
      if (typeof overlay === "string") {
        const overlayExpr = latexToDesmosExpression(overlay, { variable });
        if (overlayExpr) {
          expressions.push({
            id: `overlay-${index}`,
            latex: overlayExpr,
          });
        }
        return;
      }
      if (overlay && typeof overlay.latex === "string") {
        const overlayExpr = latexToDesmosExpression(overlay.latex, { variable });
        if (overlayExpr) {
          expressions.push({
            id: overlay.id || `overlay-${index}`,
            latex: overlayExpr,
            color: overlay.color,
          });
        }
      }
    });
  }

  return expressions;
};

export default buildDesmosExpressions;
